import { Checkbox, Form } from 'antd';
import EntityAttachments from 'components/EntityAttachments';
import EntityLogoEditor from 'components/EntityLogoEditor';
import HmsEntityFormController from 'components/Form/HmsEntityFormController';
import HmsEntityFormGridBasic from 'components/Form/HmsEntityFormGridBasic';
import HmsEntityFormGridFullWidth from 'components/Form/HmsEntityFormGridFullWidth';
import HmsField from 'components/Form/HmsField';
import HmsTabbedTables from 'components/HmsTabbedTables';
import OrganizationApiToken from 'components/Organization/OrganizationApiToken';
import OrganizationWebhook from 'components/Organization/OrganizationWebhook';
import TabsWithUrlMemory from 'components/TabsWithUrlMemory';
import useEntities from 'hooks/useEntities';
import useLoadEntity from 'hooks/useLoadEntity';
import useLoginStore from 'store/loginStore';

const Organization = () => {
  const entities = useEntities();
  const entity = entities.Organization;
  const isSystemAdmin = useLoginStore(s => s.isSystemAdmin)();
  const isAdmin = useLoginStore(s => s.isAdmin)();
  const { id, isLoading, isError, isNotFound, data, reload } = useLoadEntity({ entity });
  const { relationsData: attachmentsData, reloadRelations: reloadAttachments } = useLoadEntity({
    entity,
    relations: [
      'Attachments',
    ].join(',')
  });

  return (
    <>
      <HmsEntityFormController
        {... { entity, id, isLoading, isError, isNotFound, data, reload }}
        canDelete={isSystemAdmin}
        form={(
          <TabsWithUrlMemory
            className="no-tab-padding w-[calc(100vw-75px)] desktop:w-[calc(100vw-280px)]"
            items={[
              {
                label: `Basic`,
                children: (
                  <div className="flex flex-col desktop:flex-row w-full">
                    <div className="flex items-center justify-center ml-4 mr-6 mb-5 desktop:mb-0 ">
                      <EntityLogoEditor entity={entities.Organization} id={id} data={data} size={112} />
                    </div>
                    <div className="flex-auto">
                      <HmsEntityFormGridBasic>
                        <HmsField name="name" autoFocus />
                        <HmsField name="nick" />
                        <HmsField name="short" />
                        <HmsField name="status" />
                        <HmsField name="foundDate" />
                        <HmsField name="legalName" />
                        <HmsField name="urlSlug" />
                        <HmsField name="urlGameLinks" />
                        <HmsField name="bankAccountNumber" />
                        <HmsField name="bankAccountCurrency" />
                        <HmsField name="bankCode" />
                      </HmsEntityFormGridBasic>
                    </div>
                  </div>
                )
              },
              {
                label: `Preferences`,
                children: (
                  <>
                    <HmsEntityFormGridBasic>
                      <HmsField
                        name="newPlayerReqFields"
                        type="CHECKBOX_GROUP"
                        options={[
                          { value: 'firstName', label: 'First Name', disabled: true },
                          { value: 'lastName', label: 'Last Name', disabled: true },
                          { value: 'teamId', label: 'Team' },
                          { value: 'listPlayerLevelId', label: 'Player Level' },
                          { value: 'jerseyNumber', label: 'Jersey' },
                          { value: 'listPositionId', label: 'Position' },
                          { value: 'publicProfileUrl', label: 'Public Profile URL' },
                          { value: 'stick', label: 'Stick' },
                          { value: 'gender', label: 'Gender' },
                          { value: 'birthday', label: 'Birthday' },
                          { value: 'height', label: 'Height' },
                          { value: 'weight', label: 'Weight' },
                          { value: 'cityBirth', label: 'City of Birth' },
                          { value: 'countryBirth', label: 'Country of Birth' },
                          { value: 'email', label: 'E-mail' },
                          { value: 'phone', label: 'Phone' },
                        ]}
                      />
                      <div>
                        <div className=" text-gray-500 custom-form-label-text-size mb-[6px] relative top-[1px]">
                          Navigation Bar Preferences:
                        </div>
                          <div className="flex flex-col border-[1px] border-[#d9d9d9] rounded-md p-2 px-3 w-full">
                            <Form.Item name="showScheduler" className='mb-0 h-[22px] top-[-5px] relative' valuePropName='checked'>
                              <Checkbox name="showScheduler" className="select-none">
                                Show Scheduler
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="showInfographics" className='mb-0 h-[22px] top-[-5px] relative' valuePropName='checked'>
                              <Checkbox name="showInfographics" className="select-none">
                                Show Infographics
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="showExtras" className='mb-0 h-[22px] top-[-5px] relative' valuePropName='checked'>
                              <Checkbox name="showExtras" className="select-none">
                                Show Extras
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="seasonPlayerRegistrationEnabled" className='mb-0 h-[22px] top-[-5px] relative' valuePropName='checked'>
                              <Checkbox name="seasonPlayerRegistrationEnabled" className="select-none">
                                Enable Player Season Registration
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="playerApprovalRequired" className='mb-0 h-[22px] top-[-5px] relative' valuePropName='checked'>
                              <Checkbox name="playerApprovalRequired" className="select-none">
                                New players require admin approval
                              </Checkbox>
                            </Form.Item>
                          </div>
                      </div>
                    </HmsEntityFormGridBasic>

                    <HmsEntityFormGridFullWidth>
                      <HmsField name="mailFooterText" />
                    </HmsEntityFormGridFullWidth>
                  </>
                )
              },
              {
                label: `Extras`,
                children: (
                  <>
                    <HmsEntityFormGridFullWidth>
                      <HmsField name="extrasHtml" />
                    </HmsEntityFormGridFullWidth>
                  </>
                )
              },
              ...(isAdmin && id != '_new_' ? [{
                label: `API`,
                children: (
                  <>
                    <OrganizationApiToken organizationId={id} apiTokenCreatedAt={data?.apiTokenCreatedAt} reload={reload} />
                    <div className="border-t-[1px] border-gray-200 max-w-[1200px]" />
                    <OrganizationWebhook organizationId={id} />
                  </>
                )
              }] : []),
            ].map(tab => ({ ...tab, key: tab.label }))}
          />
        )}
        footer={id != '_new_' && (
          <div className="mt-4">
            <div className="mt-4">
              <HmsTabbedTables
                tables={[
                  {
                    title: "Attachments" + (attachmentsData?.Attachments ? (' (' + attachmentsData?.Attachments?.length + ')') : ''),
                    children: <EntityAttachments entity={entity.name} id={data?.[entity.primaryKey]} data={attachmentsData} reload={reloadAttachments} />,
                  },
                ]}
              />
            </div>
          </div>
        )}
      />
    </>
  );
}

export default Organization
